/*
 * <variant-preselect> — resolves the variant the page is already showing.
 *
 * sections/main-product.liquid ships every multi-variant PDP in a "nothing
 * picked yet" state: the hidden `id` input renders empty and the add-to-cart
 * button renders `disabled`, because on shoes a size has to be chosen
 * deliberately (product-form.js answers a bare submit with "Please select a
 * size"). Dawn's VariantSelects only leaves that state on `change`.
 *
 * That is wrong for the image pickers, where the option *is* the product photo:
 * Liquid marks the first option `selected`, so the dropdown looks resolved
 * while currentVariant is still undefined and the button stays dead.
 *
 * Replaying one `change` on the select is exactly what clicking the option
 * that is already highlighted would do, so the existing pipeline —
 * currentVariant, hidden input, button state, media, URL — runs untouched.
 */
if (!customElements.get('variant-preselect')) {
  class VariantPreselect extends HTMLElement {
    connectedCallback() {
      // Wait for the whole page, not just for <variant-selects> to be defined.
      // onVariantChange() reaches straight into <media-gallery> and
      // <pickup-availability> — both of whose scripts main-product.liquid loads
      // *below* this one (media-gallery.js:2272, pickup-availability.js:1816) —
      // and Dawn calls setActiveMedia()/fetchAvailability() unguarded. Replaying
      // while those are still unupgraded HTMLElements throws mid-pipeline, after
      // currentVariant is set but before updateVariantInput() ever runs, which
      // leaves the buy button exactly as dead as it started. Deferred scripts all
      // execute before DOMContentLoaded, so by then the pipeline is whole.
      if (document.readyState === 'complete') {
        this.replay();
      } else {
        // replay() is idempotent; `load` is only a backstop for the window
        // between DOMContentLoaded and `complete`.
        document.addEventListener('DOMContentLoaded', () => this.replay(), { once: true });
        window.addEventListener('load', () => this.replay(), { once: true });
      }
    }

    replay() {
      if (this.replayed) return;

      const select = document.getElementById(this.dataset.selectId);
      const picker = select?.closest('variant-selects');
      if (typeof picker?.onVariantChange !== 'function') return;

      // Already resolved — by ?variant= in the URL, or another picker that got
      // there first. Replaying would only repeat the section fetch.
      if (picker.currentVariant) return;

      this.replayed = true;
      // onVariantChange() reads every select in the picker, so one is enough
      // however many options the product has.
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  customElements.define('variant-preselect', VariantPreselect);
}
